import React from 'react';

type GlassItem = { name: string; ml: number; sub?: string; kind: 'beer' | 'shot' | 'wine' };

const GLASSES: GlassItem[] = [
  { name: '⅓ Pint', ml: 190, sub: 'Schooner', kind: 'beer' },
  { name: '½ Pint', ml: 284, sub: 'Half pint glass', kind: 'beer' },
  { name: 'Pint', ml: 568, sub: 'Pint glass', kind: 'beer' },
  { name: 'Single', ml: 25, sub: '25ml thimble', kind: 'shot' },
  { name: 'Double', ml: 35, sub: '35ml thimble', kind: 'shot' },
  { name: 'Small Wine', ml: 125, sub: '125ml glass', kind: 'wine' },
  { name: 'Std Wine', ml: 175, sub: '175ml glass', kind: 'wine' },
  { name: 'Large Wine', ml: 250, sub: '250ml glass', kind: 'wine' },
];

const MAX_ML = 568;

function BeerGlassSvg({ ml }: { ml: number }) {
  const hScale = ml / MAX_ML;
  const h = Math.round(hScale * 88);
  const topW = Math.round(hScale * 40 + 14);
  const botW = Math.round(topW * 0.72);
  const cx = 30;
  const padY = 105 - h;
  const tl = cx - topW / 2; const tr = cx + topW / 2;
  const bl = cx - botW / 2; const br = cx + botW / 2;
  return (
    <svg viewBox="0 0 60 108" width={40} height={72} className="overflow-visible">
      <polygon points={`${tl},${padY} ${tr},${padY} ${br},106 ${bl},106`}
        style={{ fill: 'hsl(var(--primary)/0.2)' }} />
      <polygon points={`${tl},${padY} ${tr},${padY} ${br},106 ${bl},106`}
        fill="none" stroke="hsl(var(--primary))" strokeWidth="1.5" strokeLinejoin="round" />
      <line x1={tl} y1={padY + 2} x2={tr} y2={padY + 2} stroke="hsl(var(--primary)/0.5)" strokeWidth="0.8" />
    </svg>
  );
}

function ShotGlassSvg({ ml }: { ml: number }) {
  const h = Math.round((ml / 35) * 44);
  const padY = 52 - h;
  return (
    <svg viewBox="0 0 40 56" width={28} height={40} className="overflow-visible">
      <polygon points={`8,${padY} 32,${padY} 34,54 6,54`}
        style={{ fill: 'hsl(var(--primary)/0.15)' }} />
      <polygon points={`8,${padY} 32,${padY} 34,54 6,54`}
        fill="none" stroke="hsl(var(--primary))" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

function WineGlassSvg({ ml }: { ml: number }) {
  const scale = ml / 250;
  const bowlH = Math.round(scale * 50 + 10);
  const bowlW = Math.round(scale * 22 + 8);
  const cy = 65 - bowlH;
  return (
    <svg viewBox="0 0 60 110" width={38} height={72} className="overflow-visible">
      <ellipse cx={30} cy={cy + bowlH} rx={bowlW} ry={bowlH}
        style={{ fill: 'hsl(var(--primary)/0.2)' }} stroke="hsl(var(--primary))" strokeWidth="1.5" />
      <line x1={28} y1={cy + bowlH * 2 - 2} x2={29} y2={90} stroke="hsl(var(--primary))" strokeWidth="1.5" />
      <line x1={31} y1={cy + bowlH * 2 - 2} x2={32} y2={90} stroke="hsl(var(--primary))" strokeWidth="1.5" />
      <line x1={16} y1={90} x2={44} y2={90} stroke="hsl(var(--primary))" strokeWidth="2" />
    </svg>
  );
}

function GlassItem({ g }: { g: GlassItem }) {
  return (
    <div className="flex flex-col items-center gap-1 min-w-[52px]">
      <div className="h-[80px] flex items-end justify-center">
        {g.kind === 'beer' && <BeerGlassSvg ml={g.ml} />}
        {g.kind === 'shot' && <ShotGlassSvg ml={g.ml} />}
        {g.kind === 'wine' && <WineGlassSvg ml={g.ml} />}
      </div>
      <p className="text-xs font-semibold text-foreground text-center leading-tight">{g.name}</p>
      <p className="text-[11px] text-muted-foreground text-center">{g.ml}ml</p>
      {g.sub && <p className="text-[10px] text-muted-foreground/70 text-center leading-tight">{g.sub}</p>}
    </div>
  );
}

export default function GlassGuide() {
  const beer = GLASSES.filter(g => g.kind === 'beer');
  const shots = GLASSES.filter(g => g.kind === 'shot');
  const wines = GLASSES.filter(g => g.kind === 'wine');
  return (
    <div className="my-4 border border-border rounded-xl bg-card p-5">
      <h3 className="text-sm font-bold text-foreground mb-1">📐 Glass Size Reference Guide</h3>
      <p className="text-xs text-muted-foreground mb-4">Visual comparison of all legal serve sizes. Sizes shown proportionally.</p>
      <div className="flex flex-wrap gap-8">
        <div>
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">🍺 Beer & Cider</p>
          <div className="flex gap-4 items-end">{beer.map(g => <GlassItem key={g.name} g={g} />)}</div>
        </div>
        <div>
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">🥃 Spirits</p>
          <div className="flex gap-4 items-end">{shots.map(g => <GlassItem key={g.name} g={g} />)}</div>
        </div>
        <div>
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">🍷 Wine</p>
          <div className="flex gap-4 items-end">{wines.map(g => <GlassItem key={g.name} g={g} />)}</div>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
        {[
          { icon: '🍺', tip: 'Beer/cider must reach the line on the glass. If below — always top up. It\'s a legal requirement.' },
          { icon: '🥃', tip: 'Always use a thimble or optic. Never free-pour spirits — it\'s illegal under the Weights & Measures Act.' },
          { icon: '🍷', tip: 'Confirm the wine measure with every order: "Small, standard or large glass?"' },
        ].map(t => (
          <div key={t.icon} className="bg-muted/40 rounded-lg px-3 py-2 flex gap-2 items-start">
            <span className="text-base">{t.icon}</span>
            <p className="text-[11px] text-muted-foreground leading-relaxed">{t.tip}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
